<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import AgentSelection from '@/components/home/agent-selection/agent-selection.vue';
import CapabilityError from '@/components/home/capability-error.vue';
import MainViewNext from '@/components/layouts/main-view-next.vue';
import MainView from '@/components/layouts/main-view.vue';
import AconnectAccountView from '@/components/settings/account/aconnect-account-view.vue';
import AconnectSettingsView from '@/components/settings/aconnect/aconnect-settings-view.vue';
import { useFunctionAccess } from '@/utils/composables/useFunctionAccess';
import { shouldUseNextLayout } from '@/utils/composables/useLayoutPreference';
import { featureFlags } from '@/featureFlags';

/**
 * エージェントに依存しないアカウントの画面。ルートはそのままに、シェルだけ差し替える。
 * 旧デザインでは差し替えない。
 * マイページは情報収集エージェントの中に残すため含めない。
 *
 * ANDEV_10131_CAPABILITY の削除時は、この一覧と分岐を消して、
 * 対象のルートを AconnectAccountView 配下に移す。
 */
const ACCOUNT_PATH_PREFIXES = ['/settings/profile', '/settings/password'];

/**
 * Aconnectの設定に移す画面。ルートはそのままに、シェルだけ差し替える。
 * 旧デザインでは差し替えない。
 * テーマ管理・グループ管理・環境設定は情報収集エージェントの設定に残すため含めない。
 *
 * ANDEV_10131_CAPABILITY の削除時は、この一覧と分岐を消して、
 * 対象のルートを AconnectSettingsView 配下に移す。
 */
const ACONNECT_SETTINGS_PATH_PREFIXES = [
  '/admin/contracts',
  '/admin/members',
  '/admin/user_documents',
  '/admin/ip_address_restrictions',
  '/admin/sso',
  '/admin/organization_tags',
  '/settings/organization_tags',
];

const useNextLayout = shouldUseNextLayout();
const route = useRoute();
const functionAccess = featureFlags.ANDEV_10131_CAPABILITY
  ? useFunctionAccess()
  : undefined;

const isUnderAny = (pathPrefixes: string[]) =>
  pathPrefixes.some(
    prefix => route.path === prefix || route.path.startsWith(`${prefix}/`),
  );

const isAccount = computed(() => isUnderAny(ACCOUNT_PATH_PREFIXES));
const isAconnectSettings = computed(() =>
  isUnderAny(ACONNECT_SETTINGS_PATH_PREFIXES),
);

const content = computed<
  'shell' | 'agentSelection' | 'error' | 'account' | 'aconnectSettings' | null
>(() => {
  if (!functionAccess) return 'shell';
  if (isAccount.value || isAconnectSettings.value) {
    // 旧デザインは情報収集の中だけにあるので、情報収集を使えるかが分かるまで待つ
    if (!useNextLayout && functionAccess.isLoading.value) return null;
    if (!useNextLayout && functionAccess.canUse('information_retrieval')) {
      return 'shell';
    }
    return isAccount.value ? 'account' : 'aconnectSettings';
  }
  if (route.name !== 'anewsHome') return 'shell';
  if (functionAccess.isLoading.value) return null;
  // 使えるエージェントが分からないので、どちらのホームを出しても誤りになりうる
  if (functionAccess.availableFunctions.value === null) return 'error';
  return functionAccess.canUse('information_retrieval')
    ? 'shell'
    : 'agentSelection';
});

const availableFunctions = computed(
  () => functionAccess?.availableFunctions.value ?? [],
);
</script>

<template>
  <AgentSelection
    v-if="content === 'agentSelection'"
    :available-functions="availableFunctions"
  />
  <CapabilityError v-else-if="content === 'error'" />
  <AconnectAccountView v-else-if="content === 'account'" />
  <AconnectSettingsView v-else-if="content === 'aconnectSettings'" />
  <template v-else-if="content === 'shell'">
    <MainViewNext v-if="useNextLayout" />
    <MainView v-else />
  </template>
</template>
